import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, Users, Calendar, Video, MessageSquare, Database, 
  TrendingUp, Activity, Server, Clock, RefreshCw, Download,
  UserCheck, Mail, Phone, FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminAnalyticsPage({ user, onLogout, teams = [], onSwitchTeam }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageLogs, setMessageLogs] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, [user.team_id]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [
        analyticsRes,
        messageLogsRes,
        activityRes
      ] = await Promise.all([
        axios.get(`${API}/admin/analytics?team_id=${user.team_id}`),
        axios.get(`${API}/messaging/logs?team_id=${user.team_id}&limit=20`),
        axios.get(`${API}/admin/activity?team_id=${user.team_id}&limit=20`)
      ]);

      setAnalytics(analyticsRes.data);
      setMessageLogs(messageLogsRes.data || []);
      setRecentActivity(activityRes.data?.activities || []);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      // Set default values if API fails
      setAnalytics({
        overview: { players: 0, coaches: 0, contacts: 0, games: 0, practices: 0, videos: 0 },
        messaging: { total_sent: 0, emails_sent: 0, sms_sent: 0, failed: 0 },
        engagement: { active_users: 0, page_views: 0 },
        storage: { total_files: 0, total_size_mb: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (type) => {
    try {
      toast.info(`Exporting ${type} data...`);
      const response = await axios.get(`${API}/admin/export/${type}?team_id=${user.team_id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} data exported successfully!`);
    } catch (error) {
      toast.error(`Failed to export ${type} data`);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'text-primary', subtext }) => (
    <Card className="border-2">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{title}</p>
            <p className="text-3xl font-black font-heading mt-1">{value}</p>
            {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
          </div>
          <Icon className={`h-10 w-10 ${color} opacity-30`} />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout user={user} onLogout={onLogout} teams={teams} onSwitchTeam={onSwitchTeam}>
      <div className="p-8 space-y-8" data-testid="admin-analytics-page">
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Analytics & Management
            </h1>
            <p className="text-muted-foreground mt-2">Monitor your team's data and app usage</p>
          </div>
          <Button onClick={loadAnalytics} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Database Info Banner */}
        <Card className="border-2 border-blue-300 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Database className="h-8 w-8 text-blue-600" />
              <div>
                <h3 className="font-bold text-blue-900">Data Storage</h3>
                <p className="text-sm text-blue-700">
                  All data is stored in MongoDB database. Data persists across sessions and is backed up regularly.
                </p>
              </div>
              <Badge variant="outline" className="ml-auto bg-green-100 text-green-700 border-green-300">
                <Server className="h-3 w-3 mr-1" />
                Connected
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="messaging">
              <MessageSquare className="h-4 w-4 mr-2" />
              Messaging
            </TabsTrigger>
            <TabsTrigger value="data">
              <Database className="h-4 w-4 mr-2" />
              Data Management
            </TabsTrigger>
            <TabsTrigger value="activity">
              <Activity className="h-4 w-4 mr-2" />
              Activity Log
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard 
                title="Players" 
                value={analytics?.overview?.players || 0} 
                icon={Users}
                color="text-blue-500"
              />
              <StatCard 
                title="Coaches" 
                value={analytics?.overview?.coaches || 0} 
                icon={UserCheck}
                color="text-purple-500"
              />
              <StatCard 
                title="Contacts" 
                value={analytics?.overview?.contacts || 0} 
                icon={Phone}
                color="text-green-500"
              />
              <StatCard 
                title="Games" 
                value={analytics?.overview?.games || 0} 
                icon={Calendar}
                color="text-orange-500"
              />
              <StatCard 
                title="Practices" 
                value={analytics?.overview?.practices || 0} 
                icon={Clock}
                color="text-cyan-500"
              />
              <StatCard 
                title="Videos" 
                value={analytics?.overview?.videos || 0} 
                icon={Video}
                color="text-red-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2">
                <CardHeader className="border-b bg-muted/30">
                  <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Season Stats Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Total Game Sessions</span>
                      <span className="font-bold">{analytics?.overview?.game_sessions || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Stats Recorded</span>
                      <span className="font-bold">{analytics?.overview?.stats_recorded || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Practice Plans</span>
                      <span className="font-bold">{analytics?.overview?.practice_plans || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Scouting Reports</span>
                      <span className="font-bold">{analytics?.overview?.scouting_reports || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-muted-foreground">Social Posts</span>
                      <span className="font-bold">{analytics?.overview?.social_posts || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardHeader className="border-b bg-muted/30">
                  <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Storage Usage
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Total Files</span>
                      <span className="font-bold">{analytics?.storage?.total_files || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Photos</span>
                      <span className="font-bold">{analytics?.storage?.photos || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Videos</span>
                      <span className="font-bold">{analytics?.storage?.videos || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-muted-foreground">Documents</span>
                      <span className="font-bold">{analytics?.storage?.documents || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Messaging Tab */}
          <TabsContent value="messaging" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard 
                title="Total Sent" 
                value={analytics?.messaging?.total_sent || 0} 
                icon={MessageSquare}
                color="text-blue-500"
              />
              <StatCard 
                title="Emails Sent" 
                value={analytics?.messaging?.emails_sent || 0} 
                icon={Mail}
                color="text-green-500"
              />
              <StatCard 
                title="SMS Sent" 
                value={analytics?.messaging?.sms_sent || 0} 
                icon={Phone}
                color="text-purple-500"
              />
              <StatCard 
                title="Failed" 
                value={analytics?.messaging?.failed || 0} 
                icon={MessageSquare}
                color="text-red-500"
              />
            </div>

            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="font-heading uppercase text-xl">Recent Message Logs</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-96 overflow-y-auto">
                  {messageLogs.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left p-3 text-xs uppercase">Type</th>
                          <th className="text-left p-3 text-xs uppercase">Message</th>
                          <th className="text-left p-3 text-xs uppercase">Recipients</th>
                          <th className="text-left p-3 text-xs uppercase">Status</th>
                          <th className="text-left p-3 text-xs uppercase">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {messageLogs.map((log, idx) => (
                          <tr key={log.id || idx} className="border-b hover:bg-muted/30">
                            <td className="p-3">
                              <Badge variant="outline">{log.type || 'message'}</Badge>
                            </td>
                            <td className="p-3 max-w-xs truncate text-sm">{log.title || log.message?.substring(0, 50)}</td>
                            <td className="p-3 text-sm">{log.recipients_count || 0}</td>
                            <td className="p-3">
                              <Badge className={log.status === 'sent' || log.email_sent_count > 0 ? 'bg-green-500' : 'bg-yellow-500'}>
                                {log.email_sent_count || 0} email / {log.sms_sent_count || 0} sms
                              </Badge>
                            </td>
                            <td className="p-3 text-xs text-muted-foreground">
                              {new Date(log.created_at).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      No message logs found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Management Tab */}
          <TabsContent value="data" className="space-y-6">
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Export Data
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-muted-foreground mb-4">
                  Download your team's data in CSV format for backup or external analysis.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button variant="outline" onClick={() => exportData('players')} className="h-auto py-4 flex-col">
                    <Users className="h-6 w-6 mb-2" />
                    <span>Players</span>
                  </Button>
                  <Button variant="outline" onClick={() => exportData('contacts')} className="h-auto py-4 flex-col">
                    <Phone className="h-6 w-6 mb-2" />
                    <span>Contacts</span>
                  </Button>
                  <Button variant="outline" onClick={() => exportData('schedule')} className="h-auto py-4 flex-col">
                    <Calendar className="h-6 w-6 mb-2" />
                    <span>Schedule</span>
                  </Button>
                  <Button variant="outline" onClick={() => exportData('stats')} className="h-auto py-4 flex-col">
                    <BarChart3 className="h-6 w-6 mb-2" />
                    <span>Stats</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-300 bg-amber-50">
              <CardHeader className="border-b bg-amber-100">
                <CardTitle className="font-heading uppercase text-xl text-amber-900">Database Collections</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-sm text-amber-800 mb-4">
                  Your app data is organized into the following collections in MongoDB:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  {['players', 'contacts', 'schedules', 'game_sessions', 'live_game_stats', 
                    'practice_plans', 'videos', 'photos', 'scouting_reports', 'coaches',
                    'teams', 'users', 'messages', 'social_posts'].map(col => (
                    <div key={col} className="flex items-center gap-2 p-2 bg-white rounded border">
                      <Database className="h-3 w-3 text-amber-600" />
                      <span>{col}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity" className="space-y-6">
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="font-heading uppercase text-xl flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[500px] overflow-y-auto">
                  {recentActivity.length > 0 ? (
                    <div className="divide-y">
                      {recentActivity.map((activity, idx) => (
                        <div key={idx} className="p-4 hover:bg-muted/30">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-full ${
                              activity.type === 'create' ? 'bg-green-100 text-green-600' :
                              activity.type === 'update' ? 'bg-blue-100 text-blue-600' :
                              activity.type === 'delete' ? 'bg-red-100 text-red-600' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              <FileText className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{activity.description}</p>
                              <p className="text-sm text-muted-foreground">{activity.details}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(activity.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <Activity className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>No recent activity recorded</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
